import type { ChangeEvent, DragEvent } from "react";
import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Upload } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader } from "@similarweb/ui-components";

import signalRecommenderI18nKeys from "../../utils/i18n";

import { LOADER_OPTIONS, StyledDropZone, StyledDropZoneAction, StyledDropZoneLabel, StyledHiddenInput } from "./styles";

type DealSignalCsvInputProps = {
    isValidating: boolean;
    onFileSelect: (file: File) => void;
};

export const DealSignalCsvInput = ({ isValidating, onFileSelect }: DealSignalCsvInputProps) => {
    const { translate } = useTranslate();

    const [isDragActive, setIsDragActive] = useState(false);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        // Reset the value so re-picking the same file still fires a change event.
        event.target.value = "";

        if (file) {
            onFileSelect(file);
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    /*
     * Anything dropped is accepted and then validated, exactly like anything picked. Screening by type
     * here made the two paths disagree: `accept=".csv"` is only a hint the picker may ignore, so a
     * spreadsheet reached the error step when chosen but vanished without a word when dropped.
     */
    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);

        if (isValidating) {
            return;
        }

        const file = event.dataTransfer.files?.[0];

        if (file) {
            onFileSelect(file);
        }
    };

    return (
        <StyledDropZone
            data-automation="signal-recommender-csv-dropzone"
            $isDragActive={isDragActive}
            $isDisabled={isValidating}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <StyledDropZoneLabel>
                {isValidating ? (
                    <CircularLoader options={LOADER_OPTIONS} />
                ) : (
                    <Upload
                        size="xs"
                        color={colorsPalettes.blue[400]}
                    />
                )}

                <StyledDropZoneAction>
                    {translate(
                        isValidating
                            ? signalRecommenderI18nKeys.upload.dropzone.loading
                            : signalRecommenderI18nKeys.upload.dropzone.action,
                    )}
                </StyledDropZoneAction>

                <StyledHiddenInput
                    data-automation="signal-recommender-csv-input"
                    type="file"
                    accept=".csv"
                    disabled={isValidating}
                    onChange={handleInputChange}
                />
            </StyledDropZoneLabel>
        </StyledDropZone>
    );
};
